import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, CardSkeleton, Field, Icon, PageHeader, cx,
} from '../components/ui';

// ── Maintenance freeze (superadmin) ──────────────────────────────────────────
// The global kill-switch used during migration windows. When ARMED, proxy-channels
// replies a bilingual maintenance message to EVERY inbound and forwards nothing —
// no message reaches an agent, no coupon/token activates, no command runs. It is the
// load-bearing safety switch for the stage migration (STAGE-MIGRATION-PLAN Phase 1).
// proxy-channels picks up the change on its next reload tick (~15s).

// What the freeze does, shown next to the switch. The InfoTip keeps the why (migration
// windows, sweeps); this is the what, which an operator needs in front of them before
// pressing Arm, and again when they land on the page mid-migration.
const EFFECTS = [
  { icon: 'message', text: 'Every sender gets the maintenance reply' },
  { icon: 'bot', text: 'No message reaches an agent' },
  { icon: 'key', text: 'No coupon or activation token activates' },
  { icon: 'terminal', text: 'No slash command runs' },
  { icon: 'clock', text: 'Scheduled sweeps stand down' },
];

// The backend skips an empty message rather than storing it (services/maintenance.py), on
// both the save and the arm/lift request, so an emptied field keeps whatever is live.
const EMPTY_HINT = 'Empty, so saving keeps the current message.';

// "26 Sep 2026, 14:02": a month name cannot be misread as D/M vs M/D.
function formatWhen(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// "3 h ago" beside the date: a freeze someone forgot to lift reads as "2 days ago" long
// before anyone does the date arithmetic.
function ago(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

function Maintenance() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // Which write is in flight, 'toggle' or 'save', so only that button shows a spinner.
  // Every control is disabled while either runs.
  const [busy, setBusy] = useState(null);
  const [msgIt, setMsgIt] = useState('');
  const [msgEn, setMsgEn] = useState('');
  const [confirm, confirmDialog] = useConfirm();

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch('/api/maintenance');
      if (resp.ok) {
        const data = await resp.json();
        setStatus(data);
        setMsgIt(data.message_it || '');
        setMsgEn(data.message_en || '');
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load maintenance status');
      }
    } catch {
      toast.error('Failed to load maintenance status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const enabled = !!status?.enabled;
  const itDirty = !!status && msgIt !== (status.message_it || '');
  const enDirty = !!status && msgEn !== (status.message_en || '');
  const dirty = itDirty || enDirty;

  // Arming and lifting send the two messages as well, so edits that were never saved go
  // live with the switch. The dialogs say so rather than leave it to be discovered.
  const unsavedNote = dirty
    ? <p>Your unsaved edits to the reply are saved at the same time.</p>
    : null;

  const apply = async (next) => {
    if (next && !await confirm({
      title: 'Arm the maintenance freeze?',
      body: (
        <>
          <p>
            Every bot replies the maintenance message and forwards{' '}
            <strong>nothing</strong>: no message reaches an agent, no coupon or token
            activates, no command runs.
          </p>
          <p>Scheduled sweeps stand down too. Reaches the channels in about 15 seconds.</p>
          {unsavedNote}
        </>
      ),
      confirmLabel: 'Arm freeze',
    })) return;
    // "Within about 15 seconds", not "immediately": lifting is picked up on the same
    // proxy-channels reload tick as arming.
    if (!next && !await confirm({
      title: 'Lift the maintenance freeze?',
      body: (
        <>
          <p>Inbound resumes within about 15 seconds. Messages sent while frozen are not replayed.</p>
          {unsavedNote}
        </>
      ),
      confirmLabel: 'Lift freeze',
      tone: 'primary',
    })) return;
    setBusy('toggle');
    const data = await callApi('/api/maintenance', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: next, message_it: msgIt, message_en: msgEn }),
    }, {
      successMsg: next ? 'Maintenance ARMED, inbound frozen' : 'Maintenance lifted',
      errorMsg: 'Failed to update maintenance state',
    });
    setBusy(null);
    if (data) setStatus(data);
  };

  const saveMessages = async () => {
    setBusy('save');
    const data = await callApi('/api/maintenance', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: status?.enabled ?? false, message_it: msgIt, message_en: msgEn }),
    }, { successMsg: 'Messages saved', errorMsg: 'Failed to save messages' });
    setBusy(null);
    if (data) setStatus(data);
  };

  const refreshing = loading && !!status;
  const changedAgo = status?.updated_at ? ago(status.updated_at) : null;

  const header = (
    <PageHeader
      description="Freeze the platform for a migration window. Users get the message below in both languages."
      actions={(
        <button type="button" className="btn btn-secondary" onClick={fetchStatus} disabled={loading}>
          {refreshing ? <span className="spinner-sm" /> : <Icon name="refresh" />}
          Refresh
        </button>
      )}
    >
      <h1 className="page-title">
        Maintenance
        <InfoTip label="About the maintenance freeze" width={360}>
          <p>
            For a migration window, not for pausing one user: it stops every channel, for
            everyone, at once.
          </p>
          <p>
            Scheduled sweeps stand down too, so nothing advances a schedule or prunes against
            a database mid-migration.
          </p>
          <p>
            Messages sent while it is armed get the reply and are dropped, so lifting it does
            not replay them.
          </p>
        </InfoTip>
      </h1>
    </PageHeader>
  );

  if (!status) {
    return (
      <div>
        {header}
        {loading ? (
          <>
            <CardSkeleton lines={4} />
            <CardSkeleton lines={3} />
          </>
        ) : (
          /* A failed load used to render as "Normal operation" with a live Arm button and
             a Save that would send `enabled: false`, lifting a freeze the page could not
             see. Unknown is shown as unknown, and nothing can be changed on a guess. */
          <div className="notice notice-danger" role="alert">
            <Icon name="alertCircle" />
            <div className="notice-body">
              <div className="notice-title">The freeze state did not load</div>
              The platform may or may not be frozen. The switch and the reply stay hidden
              until the state can be read.
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={fetchStatus}>
              <Icon name="refresh" /> Try again
            </button>
          </div>
        )}
        {confirmDialog}
      </div>
    );
  }

  return (
    <div>
      {header}

      {/* The state and the switch share one card, and it takes the danger-zone frame while
          the freeze is armed: the colour of the card is the state, so it cannot be missed
          by someone who only glances at the page. */}
      <section className={cx('card', enabled && 'danger-zone')} aria-labelledby="mt-state-title">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="mt-state-title" className="card-title">
              <Icon name="maintenance" className={enabled ? undefined : 'text-muted'} />
              Maintenance freeze
            </h2>
            <p className="card-subtitle">The kill switch for migration windows.</p>
          </div>
          <div className="card-actions">
            <Badge tone={enabled ? 'danger' : 'ok'} dot pulse={enabled} size="lg">
              {enabled ? 'Armed' : 'Off'}
            </Badge>
          </div>
        </div>

        <div className="row-wrap between items-start gap-4">
          <div className="stack gap-1 min-w-0" style={{ flex: '1 1 320px' }}>
            <p role="status" className={cx('text-lg fw-600 m-0', enabled ? 'text-danger' : 'text-ok')}>
              {enabled ? 'All inbound is frozen' : 'Normal operation'}
            </p>
            <p className="text-muted m-0">
              {enabled
                ? 'Every sender gets the reply below and nothing reaches an agent. Lift the freeze once the migration is done.'
                : 'Messages reach the agents, coupons and tokens activate, and commands run as usual.'}
            </p>
            {/* "Last changed", not "armed since": updated_at is the write time of the
                maintenance_mode row, and Save messages rewrites that row with its current
                value, so a message edit during a freeze moves it too. */}
            {status.updated_at && (
              <p className="text-sm text-faint m-0 inline-row">
                <Icon name="clock" size={14} />
                <span>
                  Last changed {changedAgo ? `${changedAgo}, ` : ''}
                  <time dateTime={status.updated_at}>{formatWhen(status.updated_at)}</time>
                </span>
              </p>
            )}
          </div>
          <div className="shrink-0">
            {enabled ? (
              <button type="button" className="btn btn-primary btn-lg" disabled={!!busy || loading}
                      onClick={() => apply(false)}>
                {busy === 'toggle' ? <span className="spinner-sm" /> : <Icon name="play" />}
                {busy === 'toggle' ? 'Lifting…' : 'Lift freeze'}
              </button>
            ) : (
              <button type="button" className="btn btn-danger btn-lg" disabled={!!busy || loading}
                      onClick={() => apply(true)}>
                {busy === 'toggle' ? <span className="spinner-sm" /> : <Icon name="pause" />}
                {busy === 'toggle' ? 'Arming…' : 'Arm freeze'}
              </button>
            )}
          </div>
        </div>

        <div className="card-inset mt-5">
          <div className="overline mb-3">While armed</div>
          <ul className="grid-2 gap-2">
            {EFFECTS.map((effect) => (
              <li key={effect.text} className="row gap-2">
                <Icon name={effect.icon} className={enabled ? 'text-danger' : 'text-muted'} />
                <span>{effect.text}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted mt-3 mb-0">
            Takes effect on every channel within about 15 seconds, when arming and when lifting.
          </p>
        </div>
      </section>

      <section className="card" aria-labelledby="mt-reply-title">
        <div className="card-header">
          <div className="min-w-0">
            <h2 id="mt-reply-title" className="card-title">Maintenance reply</h2>
            <p className="card-subtitle">
              Sent while the freeze is armed, as one reply: the Italian message, a blank line,
              then the English one.
            </p>
          </div>
          {dirty && (
            <div className="card-actions">
              <Badge tone="warn" dot>Unsaved changes</Badge>
            </div>
          )}
        </div>

        <div className="form-row">
          <Field label="Italian message" htmlFor="mt-msg-it" hint={msgIt.trim() ? null : EMPTY_HINT}>
            <textarea
              id="mt-msg-it"
              lang="it"
              className={cx('form-input', itDirty && 'is-dirty')}
              rows={3}
              value={msgIt}
              disabled={loading}
              onChange={(e) => setMsgIt(e.target.value)}
            />
          </Field>
          <Field label="English message" htmlFor="mt-msg-en" hint={msgEn.trim() ? null : EMPTY_HINT}>
            <textarea
              id="mt-msg-en"
              lang="en"
              className={cx('form-input', enDirty && 'is-dirty')}
              rows={3}
              value={msgEn}
              disabled={loading}
              onChange={(e) => setMsgEn(e.target.value)}
            />
          </Field>
        </div>

        <div className="form-actions mt-2">
          <span className="text-sm text-muted grow">Saving changes the reply only. It does not arm the freeze.</span>
          <button type="button" className="btn btn-primary" disabled={!!busy || loading || !dirty}
                  onClick={saveMessages}>
            {busy === 'save' ? <><span className="spinner-sm" /> Saving…</> : 'Save messages'}
          </button>
        </div>
      </section>

      {confirmDialog}
    </div>
  );
}

export default Maintenance;
